import { nullSafeToString } from '../lib/util';

/*
 * Collaterate persists a card type as an `order_cc_types` id on ProjectCcPaymentInfoDTO.cardType,
 * while Braintree reports card types as strings. Those strings arrive in more than one shape: hosted
 * fields tokenize payloads use display names ("MasterCard"), and client-side card detection uses
 * kebab-case slugs ("master-card"), so names are normalized before lookup.
 *
 * Ids verified against production `order_cc_types`. Card brands Collaterate does not carry (JCB,
 * Maestro, Diners Club, UnionPay and friends) intentionally resolve to null, matching
 * BraintreePaymentProvider, which maps them to an empty type name. A null cardType is accepted by
 * the API; a wrong one is rejected outright.
 */

const CARD_TYPE_IDS = Object.freeze({
    visa: 1,
    mastercard: 2,
    americanexpress: 3,
    amex: 3,
    discover: 4,
    paypal: 5,
    paypalaccount: 5,
});

/** The cardType value Collaterate reports for a vaulted PayPal account (BraintreePaymentConstants.PAYPAL). */
export const PAYPAL_CARD_TYPE = 'PayPal';

const normalizeCardTypeName = name => nullSafeToString(name).toLowerCase().replace(/[^a-z\d]/gu, '');

/**
 * Resolve a Braintree card type string to the numeric `order_cc_types` id the API expects.
 *
 * @param {string} braintreeCardType - Card type as reported by Braintree, in any casing or slug form.
 * @returns {?number} The matching card type id, or null when Collaterate does not carry the brand.
 */

export function getCardTypeId (braintreeCardType) {
    return CARD_TYPE_IDS[normalizeCardTypeName(braintreeCardType)] || null;
}

/**
 * Whether a Braintree card type string identifies a PayPal account rather than a card.
 *
 * @param {string} braintreeCardType - Card type as reported by Braintree or by the saved payment methods endpoint.
 * @returns {boolean}
 */

export function isPayPalCardType (braintreeCardType) {
    return normalizeCardTypeName(braintreeCardType) === normalizeCardTypeName(PAYPAL_CARD_TYPE);
}
